import { useState, useMemo, useEffect, useCallback } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { Search, Play, CheckCircle2, X } from 'lucide-react';
import { useSearchParams, useNavigate } from 'react-router-dom';

import { Pagination } from '@/components/shared/Pagination';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { useCalls } from '@/hooks/useCalls';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useReviewConfigs } from '@/hooks/useReviewConfigs';
import { useStartCallReview } from '@/hooks/useReviews';
import { formatDate } from '@/utils/format';

import type { CallQuery } from '@repo/shared-types';

export const CallReviewsPage = () => {
  usePageTitle('Call Reviews');
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Read state from URL
  const page = Number(searchParams.get('page')) || 1;
  const limit = 20;
  const isReviewedParam = searchParams.get('is_reviewed') || '';

  // Local state for search input (for debouncing)
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');
  const [debouncedSearch, setDebouncedSearch] = useState(searchParams.get('search') || '');

  const [reviewDialogState, setReviewDialogState] = useState<{
    open: boolean;
    callId: string | null;
  }>({
    open: false,
    callId: null,
  });

  const { data: reviewConfigs = [] } = useReviewConfigs();
  const [selectedConfigId, setSelectedConfigId] = useState<string>('');

  // Update URL params helper
  const updateParams = useCallback(
    (updates: Record<string, string | null>) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(updates)) {
          if (value) {
            next.set(key, value);
          } else {
            next.delete(key);
          }
        }
        return next;
      });
    },
    [setSearchParams],
  );

  // Debounce search input and sync to URL
  useEffect(() => {
    const timer = setTimeout(() => {
      const searchChanged = debouncedSearch !== searchInput;
      setDebouncedSearch(searchInput);
      updateParams({
        search: searchInput || null,
        ...(searchChanged && { page: null }),
      });
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, debouncedSearch, updateParams]);

  // Build query from state using useMemo
  const query = useMemo(() => {
    const q: Partial<CallQuery> = { page, limit };
    if (debouncedSearch) q.search = debouncedSearch;

    // Add review status filter
    if (isReviewedParam === 'true') {
      q.is_reviewed = true;
    } else if (isReviewedParam === 'false') {
      q.is_reviewed = false;
    }

    return q;
  }, [page, limit, debouncedSearch, isReviewedParam]);

  const { data: callsData, isLoading } = useCalls(query);
  const startCallReviewMutation = useStartCallReview();

  const calls = callsData?.data ?? [];
  const pagination = callsData?.pagination;

  const clearFilters = () => {
    setSearchInput('');
    setDebouncedSearch('');
    setSearchParams(new URLSearchParams());
  };

  const hasActiveFilters = searchInput || isReviewedParam;

  const handleOpenReviewDialog = (callId: string) => {
    setReviewDialogState({ open: true, callId });
    setSelectedConfigId('');
  };

  const handleStartReview = async () => {
    if (!reviewDialogState.callId) return;

    try {
      await startCallReviewMutation.mutateAsync({
        callId: reviewDialogState.callId,
        reviewConfigId: selectedConfigId || undefined,
      });

      toast({
        variant: 'success',
        title: 'Review started',
        description: 'AI review process has been initiated for this call.',
      });

      setReviewDialogState({ open: false, callId: null });
      queryClient.invalidateQueries({ queryKey: ['calls'] });
      navigate(`/reviews/call/${reviewDialogState.callId}`);
    } catch (error: unknown) {
      toast({
        variant: 'destructive',
        title: 'Failed to start review',
        description: error instanceof Error ? error.message : 'An error occurred',
      });
    }
  };

  const getOutcomeBadge = (outcome: string) => {
    const styles: Record<string, string> = {
      PICKED_UP: 'bg-emerald-100 text-emerald-800',
      NO_ANSWER: 'bg-gray-100 text-gray-800',
      VOICEMAIL: 'bg-blue-100 text-blue-800',
      BUSY: 'bg-orange-100 text-orange-800',
      AVAILABILITY_CAPTURED: 'bg-green-100 text-green-800',
      LEAD_CAPTURED: 'bg-purple-100 text-purple-800',
    };
    return styles[outcome] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-8">
      <header>
        <p className="text-muted-foreground text-sm tracking-[0.3em] uppercase">Reviews</p>
        <h1 className="text-3xl font-bold tracking-tight">Call Reviews</h1>
        <p className="text-muted-foreground mt-2">
          Review individual call recordings with AI analysis
        </p>
      </header>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="min-w-[300px] flex-1">
          <label className="text-muted-foreground mb-1 block text-sm font-medium">Search</label>
          <div className="relative">
            <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search by phone number or contact name..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="border-input bg-background placeholder:text-muted-foreground focus-visible:ring-ring flex h-10 w-full rounded-md border px-3 py-2 pl-9 text-sm focus-visible:ring-2 focus-visible:outline-none"
            />
          </div>
        </div>
        <div className="min-w-37.5">
          <label
            htmlFor="review-status-filter"
            className="text-muted-foreground mb-1 block text-sm font-medium"
          >
            Review Status
          </label>
          <select
            id="review-status-filter"
            value={isReviewedParam}
            onChange={(e) => updateParams({ is_reviewed: e.target.value || null, page: null })}
            className="focus:ring-primary bg-background text-foreground flex h-10 w-full rounded-md border px-3 py-2 text-sm focus:ring-2 focus:outline-none"
          >
            <option value="">All calls</option>
            <option value="false">Not reviewed</option>
            <option value="true">Reviewed</option>
          </select>
        </div>
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters} className="mt-auto">
            <X className="mr-1 h-4 w-4" />
            Clear filters
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>
            {isReviewedParam === 'true' && 'Reviewed Calls'}
            {isReviewedParam === 'false' && 'Calls Pending Review'}
            {!isReviewedParam && 'All Calls with Recordings'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex min-h-[200px] items-center justify-center">
              <div className="text-center">
                <div className="border-primary mx-auto h-8 w-8 animate-spin rounded-full border-b-2" />
                <p className="text-muted-foreground mt-2 text-sm">Loading calls...</p>
              </div>
            </div>
          ) : calls.length === 0 ? (
            <div className="text-muted-foreground rounded-lg border border-dashed p-6 text-center text-sm">
              No calls found.{' '}
              {hasActiveFilters
                ? 'Try adjusting your filters.'
                : 'Calls will appear here once they have recordings.'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="text-muted-foreground text-left text-xs tracking-wide uppercase">
                    <th className="border-b px-3 py-2">Contact</th>
                    <th className="border-b px-3 py-2">Phone Number</th>
                    <th className="border-b px-3 py-2">Campaign</th>
                    <th className="border-b px-3 py-2">Outcome</th>
                    <th className="border-b px-3 py-2">Review Status</th>
                    <th className="border-b px-3 py-2">Duration</th>
                    <th className="border-b px-3 py-2">Executed at</th>
                    <th className="border-b px-3 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {calls.map((call) => (
                    <tr key={call.id} className="align-middle">
                      <td className="border-b px-3 py-2 font-medium">
                        {call.contact?.name || 'Unknown'}
                      </td>
                      <td className="border-b px-3 py-2">
                        <span className="font-mono text-xs">{call.phone_number}</span>
                      </td>
                      <td className="border-b px-3 py-2">{call.campaign?.name || '-'}</td>
                      <td className="border-b px-3 py-2">
                        <span
                          className={`rounded-full px-2 py-1 text-xs font-medium ${getOutcomeBadge(call.outcome || 'NO_ANSWER')}`}
                        >
                          {call.outcome?.replace(/_/g, ' ') || 'NO_ANSWER'}
                        </span>
                      </td>
                      <td className="border-b px-3 py-2">
                        {call.is_reviewed ? (
                          <Badge
                            variant="outline"
                            className="gap-1 border-emerald-200 bg-emerald-50 text-emerald-700"
                          >
                            <CheckCircle2 className="h-3 w-3" />
                            Reviewed
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-gray-50 text-gray-600">
                            Not Reviewed
                          </Badge>
                        )}
                      </td>
                      <td className="border-b px-3 py-2">
                        {call.duration
                          ? `${Math.floor(call.duration / 60)}:${String(call.duration % 60).padStart(2, '0')}`
                          : '-'}
                      </td>
                      <td className="text-muted-foreground border-b px-3 py-2">
                        {formatDate(call.executed_at)}
                      </td>
                      <td className="border-b px-3 py-2">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/reviews/call/${call.id}`)}
                          >
                            <CheckCircle2 className="mr-1.5 h-4 w-4" />
                            History
                          </Button>
                          <Button size="sm" onClick={() => handleOpenReviewDialog(call.id)}>
                            <Play className="mr-1.5 h-4 w-4" />
                            Start Review
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {pagination && calls.length > 0 && (
            <div className="mt-4">
              <Pagination
                page={pagination.page}
                totalPages={pagination.totalPages}
                total={pagination.total}
                limit={pagination.limit}
                onPageChange={(p) => updateParams({ page: p > 1 ? String(p) : null })}
                resourceName="calls"
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Review Config Selection Dialog */}
      <Dialog
        open={reviewDialogState.open}
        onOpenChange={(open) => !open && setReviewDialogState({ open: false, callId: null })}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Start Call Review</DialogTitle>
            <DialogDescription>
              Select a review configuration to use for analyzing this call.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Review Configuration</label>
              <select
                value={selectedConfigId}
                onChange={(e) => setSelectedConfigId(e.target.value)}
                className="border-input bg-background flex h-10 w-full rounded-md border px-3 py-2 text-sm"
              >
                <option value="">Use default configuration</option>
                {reviewConfigs.map((config) => (
                  <option key={config.id} value={config.id}>
                    {config.name} {config.is_default && '(Default)'}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setReviewDialogState({ open: false, callId: null })}
            >
              Cancel
            </Button>
            <Button onClick={handleStartReview} disabled={startCallReviewMutation.isPending}>
              {startCallReviewMutation.isPending ? 'Starting...' : 'Start Review'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
